import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import { ToursApiParams, ToursSearchState, TourResult } from '@model/tours';
import { DealFinderApi } from '@model/iceberg/service/deal-finder/deal-finder-api';
import { PackagesParams, PackagesResponse, Results } from '@model/iceberg/deal-finder/deal-finder';
import { FiltersAdapter } from '@model/iceberg/filters/deal-finder/filters-adapter';
import { RequestType, setLoading } from '@state/slices/app';
import { TripTypeFilterKeys } from '@model/common';
import { getErrorCode } from '@util/error';
import { getTourSpecialOffersQuery } from '@util/tours';
import { GlobalAppState } from '@model/state';
import { TourActions } from '@state/slices/tours/tourActions';

export enum ToursActions {
  FETCH_TOURS = '@TOURS/FETCH_TOURS',
  GET_TOURS_TOKEN = '@TOURS/GET_TOURS_TOKEN',
  GET_PACKAGES = '@TOURS/GET_PACKAGES',
  SET_FILTERS = '@TOURS/SET_FILTERS',
  LOAD_MORE = '@TOURS/LOAD_MORE'
}

export const INITIAL_TOURS_STATE: ToursSearchState = {
  data: {
    searchToken: '',
    expires: '',
    results: [],
    facets: {
      filters: [],
      counts: {
        total: 0,
        filtered: 0
      }
    },
    nextSet: 0
  },
  loading: false,
  error: null
};

const buildToursResult = (packagesResult: Results, searchToken: string) => ({
  ...packagesResult,
  searchToken
});

export const mergeToursFilters = (state: ToursSearchState, payload: any) => {
  const newState: ToursSearchState = cloneDeep(state);
  newState.data.facets.filters = newState.data.facets.filters.map((filter) => {
    const updatedFilter = payload[filter.name];
    const selectedValues = (updatedFilter || '').split(',');
    filter.values = filter.values.map((value) => {
      value.selected = selectedValues.includes(value.value);
      return value;
    });
    return filter;
  });
  return newState;
};

// Async Thunks
export const getToursToken = createAsyncThunk(
  ToursActions.GET_TOURS_TOKEN,
  async (params: ToursApiParams, { rejectWithValue }) => {
    try {
      const dealFinderAPI = new DealFinderApi();
      const response = await dealFinderAPI.getTourSearchToken(params);
      return response.searchToken;
    } catch (error: any) {
      return rejectWithValue(getErrorCode(error));
    }
  }
);

export const getPackages = createAsyncThunk(
  ToursActions.GET_PACKAGES,
  async (payload: PackagesParams, { rejectWithValue }) => {
    try {
      const api = new DealFinderApi();
      const response: PackagesResponse = await api.packages(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorCode(error));
    }
  }
);

export const fetchTours = createAsyncThunk(
  ToursActions.FETCH_TOURS,
  async (params: ToursApiParams, { dispatch, rejectWithValue }) => {
    try {
      const { searchToken, searchType } = params;
      const specialOffersQuery = getTourSpecialOffersQuery(params);

      // Safe access to TripTypeFilterKeys with type guard
      const searchTypeFilter =
        searchType && searchType in TripTypeFilterKeys
          ? TripTypeFilterKeys[searchType as keyof typeof TripTypeFilterKeys]
          : undefined;

      const toursQuery = {
        ...specialOffersQuery,
        ...(searchTypeFilter && { trip_type: searchTypeFilter })
      };

      if (searchToken) {
        const packagesResult: Results = await dispatch(getPackages({ searchToken, query: toursQuery })).unwrap();
        return buildToursResult(packagesResult, searchToken);
      } else {
        const { filters, ...packageParams } = params;
        const token = await dispatch(getToursToken(packageParams)).unwrap();
        const packagesResult: Results = await dispatch(
          getPackages({ searchToken: token, query: { ...toursQuery, ...filters } })
        ).unwrap();
        return buildToursResult(packagesResult, token);
      }
    } catch (error: any) {
      return rejectWithValue(getErrorCode(error));
    }
  }
);

export const setFilters = createAsyncThunk(
  ToursActions.SET_FILTERS,
  async (query: Record<string, string>, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as GlobalAppState;
      const { searchToken } = state.searchResults.tours.data;

      const packagesResult = await dispatch(getPackages({ searchToken, query })).unwrap();
      return { packagesResult, query };
    } catch (error: any) {
      return rejectWithValue(getErrorCode(error));
    }
  }
);

export const loadMoreTours = createAsyncThunk<
  {
    searchToken: string;
    expires: string;
    results: TourResult[];
    facets: any;
    nextSet: number;
  },
  void,
  { rejectValue: string }
>(ToursActions.LOAD_MORE, async (_, { getState, dispatch, rejectWithValue }) => {
  try {
    dispatch(setLoading({ loading: true, request: RequestType.LOAD_MORE_RESULTS }));

    const state = getState() as GlobalAppState;
    const {
      searchToken,
      nextSet,
      results,
      facets: { filters }
    } = state.searchResults.tours.data;

    const query: Record<string, string> = FiltersAdapter.getQuery(filters);
    const packagesResult = await dispatch(getPackages({ searchToken, nextSet, query })).unwrap();

    const nextSetResults: Array<TourResult> = packagesResult.results as unknown as Array<TourResult>;
    const updatedResults: Array<TourResult> = results.concat(nextSetResults);

    dispatch(setLoading({ loading: false, request: RequestType.LOAD_MORE_RESULTS }));

    return {
      ...packagesResult,
      results: updatedResults
    };
  } catch (error: any) {
    dispatch(setLoading({ loading: false, request: RequestType.LOAD_MORE_RESULTS }));
    return rejectWithValue(getErrorCode(error));
  }
});

const toursSlice = createSlice({
  name: TourActions.SLICE_NAME,
  initialState: INITIAL_TOURS_STATE,
  reducers: {
    setToursLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    clearTours: () => INITIAL_TOURS_STATE
  },
  extraReducers: (builder) => {
    builder
      // Get Tours Token
      .addCase(getToursToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getToursToken.fulfilled, (state, action) => {
        state.data.searchToken = action.payload;
        state.error = null;
      })
      .addCase(getToursToken.rejected, (state, action) => {
        state.data.searchToken = '';
        state.error = action.payload as string;
        state.loading = false;
      })

      // Get Packages
      .addCase(getPackages.fulfilled, (state, action: any) => {
        state.data = { ...state.data, ...action.payload };
        state.error = null;
        state.loading = false;
      })
      .addCase(getPackages.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })

      // Fetch Tours
      .addCase(fetchTours.pending, () => {
        return { ...INITIAL_TOURS_STATE, loading: true };
      })
      .addCase(fetchTours.fulfilled, (state, action: any) => {
        state.data = { ...state.data, ...action.payload };
        state.error = null;
        state.loading = false;
      })
      .addCase(fetchTours.rejected, (state, action) => {
        state.data = INITIAL_TOURS_STATE.data;
        state.error = action.payload as string;
        state.loading = false;
      })

      // Set Filters - this matches the saga's handleSetFilters behavior
      .addCase(setFilters.pending, (state, action) => {
        // Merge the filters into state and set loading to true
        const updatedState = mergeToursFilters(state, action.meta.arg);
        return { ...updatedState, loading: true };
      })
      .addCase(setFilters.fulfilled, (state, action: any) => {
        // Update with the API response data
        state.data = { ...state.data, ...action.payload.packagesResult };
        state.error = null;
        state.loading = false;
      })
      .addCase(setFilters.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })

      // Load More Tours
      .addCase(loadMoreTours.fulfilled, (state, action) => {
        state.data = { ...state.data, ...action.payload };
        state.error = null;
      })
      .addCase(loadMoreTours.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const { setToursLoading, clearTours } = toursSlice.actions;

// Legacy exports for backward compatibility
export const receiveToursTokenFailure = (error?: string) => ({
  type: getToursToken.rejected.type,
  payload: error
});

export const clearSearchResultsTours = () => clearTours();

// Export selector for backward compatibility
export { getToursSearchToken } from './toursSelectors';

export const toursActions = {
  fetchTours,
  getToursToken,
  getPackages,
  setFilters,
  loadMoreTours,
  setToursLoading,
  clearTours
};
export const toursReducer = toursSlice.reducer;
